import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { TreeLinkStore } from '../store/TreeLinkStore';
import * as styles from './TreeLinkNode.css';
import * as constants from '../../../global/Constants';
import { IAIcon } from '../../icon/Icon';
import { LocalizationService } from '../../../services/LocalizationService';
import { Helper } from '../../../Helper';
import { ITreeLinkNode } from '../../../interfaces/ITreeLinkNode';
import { Environment } from '../../../services/Environment';
import { IFontSlots } from '../../../interfaces/IFontSlots';

export interface ITreeLinkNodeProps {
  treeLinkStore?: TreeLinkStore;
  node: ITreeLinkNode;
  showDropZoneForId: string;
  index?: number;
  fontSlots?: IFontSlots;
  removeTree?: () => void;
  rerender: () => void;
  onClick: (node: ITreeLinkNode) => void;
  onDragEnter: (node: ITreeLinkNode) => void;
  onDrop: (node: ITreeLinkNode) => void;
  onDrag: (node: ITreeLinkNode) => void;
  showPage: (page: string) => void;
}

export interface ITreeLinkNodeState {
  showAllFirstLevelItems: boolean;
  expanded: boolean;
}

@inject('treeLinkStore')
@observer
export class TreeLinkNode extends React.Component<ITreeLinkNodeProps, ITreeLinkNodeState> {

  private localizationService: LocalizationService = new LocalizationService();
  private ref;

  constructor(props: ITreeLinkNodeProps) {
    super(props);
    this.state = {
      showAllFirstLevelItems: this.props.treeLinkStore.amountOfFirstLevelItemsToShow === undefined,
      expanded: (!this.props.node.config.isCollapsed && this.props.node.config.nodeLevel === 0) || this.props.treeLinkStore.inEditMode || this.props.treeLinkStore.expandAsDefault,
    }
    this.ref = React.createRef();
    this.setWrapperRef = this.setWrapperRef.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }



  private isChildCurrentPage(node: ITreeLinkNode): void {
    if (node.children) {
      node.children.forEach((child) => {
        if (child.config.link && (location.href === encodeURI(child.config.link) || location.href === child.config.link)) {
          this.setState({ expanded: true });
        }
        if (child.children && child.children.length > 0) {
          this.isChildCurrentPage(child);
        }
      })
    }
  }

  private setWrapperRef(node) {
    this.ref = node;
  }

  private handleClickOutside(event) {
    if (this.ref && this.ref.current && !this.ref.current.contains(event.target)) {
      this.escFunction();
    }
  }

  private keyListener(event: KeyboardEvent): void {
    if (event.keyCode === constants.ESC_KEY_CODE) {
      this.escFunction();
    }
  }

  public componentWillUnmount(): void {
    document.removeEventListener("keydown", (event) => this.keyListener(event), false);
    document.addEventListener('click', this.handleClickOutside, true);
  }

  public componentDidMount(): void {
    document.addEventListener("keydown", (event) => this.keyListener(event), false);
    document.addEventListener('click', this.handleClickOutside, true);
    this.isChildCurrentPage(this.props.node);
    this.checkLocalizedStrings();
  }

  private checkLocalizedStrings(): void {
    if (!this.localizationService?.strings?.TreeLink_SeeAll) {
      setTimeout(() => {
        this.localizationService = new LocalizationService();
        this.checkLocalizedStrings();
      }, 1000);
    } else {
      this.forceUpdate();
    }
  }

  private escFunction(): void {
    this.props.treeLinkStore.collapseAllContextMenu(this.props.treeLinkStore.treeLink);
    this.forceUpdate();
  }

  private addCommandBarItems(): JSX.Element[] {
    const commandBarItems: JSX.Element[] = [
      <div
        onClick={() => {
          this.props.treeLinkStore.createNewNode(this.props.node);
        }}
      >
        <span>{this.localizationService.strings.TreeLink_AddNew}</span>
      </div>,
      <div
        onClick={() => {
          this.props.treeLinkStore.setSelectedNodeWithParent(this.props.node, this.props.node.config.parent, true);
        }}
      >
        <span>{this.localizationService.strings.TreeLink_Edit}</span>
      </div>
    ];
    if (this.props.node.config.nodeLevel > 0) {
      commandBarItems.push(
        <div
          key={`CommandBarItemRemove_${this.props.node.config.id}`}
          onClick={() => {
            this.props.treeLinkStore.removeNode(this.props.node);
            this.props.rerender();
          }}
        >
          <span>{this.localizationService.strings.TreeLink_Remove}</span>
        </div>
      );
      if (this.props.treeLinkStore.canMoveNode(this.props.node, "up")) {
        commandBarItems.push(
          <div
            key={`CommandBarItemMoveUp_${this.props.node.config.id}`}
            onClick={() => {
              this.props.treeLinkStore.moveNode(this.props.node, "up");
              this.props.rerender();
            }}
          >
            <span>{this.localizationService.strings.TreeLink_MoveUp}</span>
          </div>
        );
      }
      if (this.props.treeLinkStore.canMoveNode(this.props.node, "down")) {
        commandBarItems.push(
          <div
            key={`CommandBarItemMoveDowm_${this.props.node.config.id}`}
            onClick={() => {
              this.props.treeLinkStore.moveNode(this.props.node, "down");
              this.props.rerender();
            }}
          >
            <span>{this.localizationService.strings.TreeLink_MoveDown}</span>
          </div>
        );
      }
    }
    if (this.props.node.config.nodeLevel === 0) {
      commandBarItems.push(
        <div
          key={`CommandBarItemRemove_${this.props.node.config.id}`}
          onClick={() => {
            this.props.removeTree();
            this.props.rerender();
          }}
        >
          <span>{this.localizationService.strings.TreeLink_Remove}</span>
        </div>
      );
    }
    if (this.props.node.config.nodeLevel > 1) {
      commandBarItems.push(
        <div
          key={`CommandBarItemPromote_${this.props.node.config.id}`}
          onClick={() => {
            this.props.treeLinkStore.promoteNode(this.props.node);
            this.props.rerender();
          }}
        >
          <span>{this.localizationService.strings.TreeLink_Promote}</span>
        </div>
      );
    }
    if (this.props.treeLinkStore.canDemote(this.props.node)) {
      commandBarItems.push(
        <div
          key={`CommandBarItemDemote_${this.props.node.config.id}`}
          onClick={() => {
            this.props.treeLinkStore.demoteNote(this.props.node);
            this.props.rerender();
          }}
        >
          <span>{this.localizationService.strings.TreeLink_Demote}</span>
        </div>
      );
    }
    return commandBarItems;
  }

  public render(): JSX.Element {
    const isHeader = this.props.node.config.nodeLevel === 0;
    const className = `${isHeader ? [styles.treeNodeHeader, styles.treeNodeItem].join(" ") : styles.treeNodeItem}`;
    const commandBarItems: JSX.Element[] = this.addCommandBarItems();
    if (this.props.node.config.title === undefined) {
      setTimeout(() => {
        setTimeout(() => {
          this.props.treeLinkStore.setIsCreatingNew(true);
        }, 0);
        this.props.treeLinkStore.setSelectedNodeWithParent(this.props.node, this.props.node.config.parent, true);
        if (document.getElementById("IA_treeViewEditPanelTitleField")) {
          document.getElementById("IA_treeViewEditPanelTitleField").focus();
        }
      }, 1000);
    }
    let modifiedDate: Date = this.props.treeLinkStore.instance.modified;
    if (this.props.treeLinkStore.instance.modified && (typeof this.props.treeLinkStore.instance.modified === 'string')) {
      modifiedDate = new Date(this.props.treeLinkStore.instance.modified);
    }
    const thumbnailImage = Helper.getImageFromApiWhenOutsideSharepoint(
      Environment.getEnvironmentForImageAPI(this.props.treeLinkStore.environment),
      this.props.treeLinkStore.tenant,
      "000",
      this.props.node.config.image,
      modifiedDate ? modifiedDate.toISOString().replace(/[^\d]/g, '') : "",
      "StandardLarge",
      this.props.node?.config?.cropSettings,
      false
    );
    return (
      <>
        <div
          id={`IA_TreeLink_${this.props.node.config.id}`}
          onDrag={() => {
            this.props.onDrag(this.props.node);
          }}
          onDragEnd={(e) => {
            e.preventDefault();
            this.props.onDrop(this.props.node);
          }}
          style={{
            display: "inline-grid",
            borderBottom: this.props.node.config.nodeLevel === 1 && this.props.treeLinkStore.showLine ? `1px solid ${this.props.treeLinkStore.lineColor}` : "none",
            width: "100%",
            color: this.props.treeLinkStore.textColor ? this.props.treeLinkStore.textColor : (this.props.treeLinkStore.hasDarkBackground ? "#ffffff" : "#333333"),
            fontFamily: isHeader ? this.props.fontSlots?.heading?.fontFamily : this.props.fontSlots?.body?.fontFamily
          }}>
          {this.props.node.config.image &&
            <>
              {this.props.treeLinkStore.inEditMode ?
                <div
                  className={styles.IA_coverImage}
                  style={{
                    backgroundImage: `url("${thumbnailImage}")`,
                    cursor: "pointer",
                    marginLeft: (this.props.treeLinkStore.backgroundColor !== undefined && this.props.treeLinkStore.backgroundColor !== null && this.props.treeLinkStore.backgroundColor !== "" && this.props.treeLinkStore.backgroundColor !== "transparent") || this.props.treeLinkStore.dropShadow ? -20 : 0,
                    width: (this.props.treeLinkStore.backgroundColor !== undefined && this.props.treeLinkStore.backgroundColor !== null && this.props.treeLinkStore.backgroundColor !== "" && this.props.treeLinkStore.backgroundColor !== "transparent") || this.props.treeLinkStore.dropShadow ? "calc(100% + 40px)" : "100%",
                    borderRadius: `${this.props.treeLinkStore.borderRadius} ${this.props.treeLinkStore.borderRadius} 0px 0px`,
                  }}
                  onClick={() => {
                    if (this.props.treeLinkStore.inEditMode) {
                      this.props.treeLinkStore.setSelectedNodeWithParent(this.props.node, this.props.node.config.parent, true);
                    }
                  }}
                />
                :
                <div>
                  {this.props.node.type !== "Text" ?
                    <a
                      target={this.props.node.config.openNewTab || this.props.treeLinkStore.openAllLinksExternally ? "_blank" : "_self"}
                      data-interception={this.props.node.config.openNewTab || this.props.treeLinkStore.openAllLinksExternally ? "off" : "on"}
                      href={this.props.node.config.link}
                      onClick={(e) => {
                        if (this.props.treeLinkStore.isMobile && !this.props.node.config.openNewTab && !this.props.treeLinkStore.openAllLinksExternally) {
                          e.preventDefault();
                        }
                        console.log("Link clicked");
                        this.props.onClick(this.props.node)
                      }}
                    >
                      <div
                        className={styles.IA_coverImage}
                        style={{
                          backgroundImage: `url("${thumbnailImage}")`,
                          cursor: this.props.node.config.link !== undefined ? "pointer" : "default",
                          marginLeft: (this.props.treeLinkStore.backgroundColor !== undefined && this.props.treeLinkStore.backgroundColor !== null && this.props.treeLinkStore.backgroundColor !== "" && this.props.treeLinkStore.backgroundColor !== "transparent") || this.props.treeLinkStore.dropShadow ? -20 : 0,
                          width: (this.props.treeLinkStore.backgroundColor !== undefined && this.props.treeLinkStore.backgroundColor !== null && this.props.treeLinkStore.backgroundColor !== "" && this.props.treeLinkStore.backgroundColor !== "transparent") || this.props.treeLinkStore.dropShadow ? "calc(100% + 40px)" : "100%",
                          borderRadius: `${this.props.treeLinkStore.borderRadius} ${this.props.treeLinkStore.borderRadius} 0px 0px`,
                        }}
                      />
                    </a>
                    :
                    <div
                      className={styles.IA_coverImage}
                      style={{
                        backgroundImage: `url("${thumbnailImage}")`,
                        cursor: this.props.node.config.link !== undefined ? "pointer" : "default",
                        marginLeft: (this.props.treeLinkStore.backgroundColor !== undefined && this.props.treeLinkStore.backgroundColor !== null && this.props.treeLinkStore.backgroundColor !== "" && this.props.treeLinkStore.backgroundColor !== "transparent") || this.props.treeLinkStore.dropShadow ? -20 : 0,
                        width: (this.props.treeLinkStore.backgroundColor !== undefined && this.props.treeLinkStore.backgroundColor !== null && this.props.treeLinkStore.backgroundColor !== "" && this.props.treeLinkStore.backgroundColor !== "transparent") || this.props.treeLinkStore.dropShadow ? "calc(100% + 40px)" : "100%",
                        borderRadius: `${this.props.treeLinkStore.borderRadius} ${this.props.treeLinkStore.borderRadius} 0px 0px`,
                      }}
                    />
                  }
                </div>
              }
            </>
          }
          <div
            style={{
              borderBottom: isHeader && this.props.treeLinkStore.showLine ? `1px solid ${this.props.treeLinkStore.lineColor}` : "none"
            }}
          >
            <div
              style={{
                width: this.props.node.config.nodeLevel > 0 ? 20 : 2,
                height: 30,
                float: "left"
              }}
            >
              {this.props.node.config.nodeLevel > 0 && this.props.node.children && this.props.node.children.length > 0 &&
                <IAIcon
                  url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Right/SVG/ic_fluent_chevron_right_48_regular.svg"
                  size={14}
                  color={this.props.treeLinkStore.highlightColor}
                  onClick={() => {
                    this.setState({ expanded: !this.state.expanded });
                    this.props.treeLinkStore.toggleCollapse(this.props.node);
                    this.forceUpdate();
                  }}
                  style={{
                    float: "left",
                    cursor: "pointer",
                    marginRight: 0,
                    marginTop: 6,
                    transition: "transform .3s ease-in-out",
                    transform: this.state.expanded ? "rotate(90deg)" : ""
                  }}
                />
              }
            </div>
            <div
              className={className}
              style={{
                cursor: this.props.treeLinkStore.inEditMode ? "pointer" : "default",
                float: "left",
                width: this.props.treeLinkStore.inEditMode ? (this.props.node.config.nodeLevel > 0 ? "calc(100% - 80px)" : "calc(100% - 58px)") : (this.props.node.config.nodeLevel > 0 ? "calc(100% - 20px)" : "calc(100% - 2px)")
              }}
              onClick={() => {
                if (this.props.treeLinkStore.inEditMode) {
                  this.props.treeLinkStore.setSelectedNodeWithParent(this.props.node, this.props.node.config.parent, true);
                }
              }}
            >
              {this.props.node.config.nodeLevel !== 0 && this.props.node.config.icon && this.props.treeLinkStore.showIconOnItems &&
                <IAIcon
                  color={this.props.treeLinkStore.iconColor ? this.props.treeLinkStore.iconColor : (this.props.treeLinkStore.hasDarkBackground ? "#ffffff" : "#333333")}
                  size={16}
                  style={{ marginTop: 1, marginRight: 5 }}
                  title={this.props.node.config.icon}
                />
              }
              {this.props.treeLinkStore.inEditMode && this.props.node.targeting.enabled &&
                <IAIcon
                  color={this.props.treeLinkStore.editHighlightColor}
                  size={14}
                  style={{ marginTop: 2, marginRight: 7, opacity: 0.6 }}
                  url="https://intraactivestorage.blob.core.windows.net/cdn/icons/People%20Checkmark/SVG/ic_fluent_people_checkmark_24_regular.svg"
                />
              }
              {this.props.treeLinkStore.inEditMode && this.props.treeLinkStore.isDragging &&
                <div
                  className={styles.IA_dropArea}
                  style={{
                    width: `calc(100% - ${this.props.node.config.nodeLevel * 20}px)`
                  }}
                  onDragEnter={() => {
                    this.props.onDragEnter(this.props.node);
                  }}
                  onDragLeave={() => {
                    this.props.onDragEnter(undefined);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                  }}
                />
              }
              {this.props.treeLinkStore.inEditMode ?
                <span style={{
                  opacity: this.props.node.config.title === undefined || this.props.node.config.title === "-" ? 0.3 : 1
                }}>
                  {this.props.node.config.title !== undefined && this.props.node.config.title !== "-" ? this.props.node.config.title : this.localizationService.strings.TreeLink_HeaderPlaceholder}
                </span>
                :
                <a
                  target={this.props.node.config.openNewTab || this.props.treeLinkStore.openAllLinksExternally ? "_blank" : "_self"}
                  data-interception={this.props.node.config.openNewTab || this.props.treeLinkStore.openAllLinksExternally ? "off" : "on"}
                  href={this.props.node.type == "Link" ? this.props.node.config.link : undefined}
                  style={{
                    color: this.props.treeLinkStore.textColor ? this.props.treeLinkStore.textColor : (this.props.treeLinkStore.hasDarkBackground ? "#ffffff" : "#333333"),
                    overflow: "hidden",
                    cursor: this.props.node.type == "Text" || this.props.node.config.link && (location.href === encodeURI(this.props.node.config.link) || location.href === this.props.node.config.link) ? "default" : "pointer",
                    fontWeight: isHeader || this.props.node.config.link && (location.href === encodeURI(this.props.node.config.link) || location.href === this.props.node.config.link) ? "bold" : "normal",
                  }}
                  onClick={(e) => {
                    if (this.props.treeLinkStore.isMobile && !this.props.node.config.openNewTab && !this.props.treeLinkStore.openAllLinksExternally) {
                      e.preventDefault();
                    }
                    console.log("Link clicked");
                    this.props.onClick(this.props.node);
                  }}
                >
                  {this.props.node.config.title}
                </a>
              }
            </div>
            {this.props.treeLinkStore.inEditMode &&
              <div style={{
                float: "right",
                fontSize: ".75rem",
                height: 27,
                backgroundColor: this.props.node.config.isContextMenuExpanded ? "#eeeeee" : "transparent"
              }}>
                <span
                  className={styles.treeLinkNodeContextMenu}
                  style={{
                    float: "right",
                    cursor: "pointer"
                  }}
                >
                  <IAIcon
                    title="More"
                    size={18}
                    style={{
                      position: "inherit",
                      padding: 5
                    }}
                    color={this.props.treeLinkStore.editHighlightColor}
                    onClick={() => {
                      this.props.treeLinkStore.toggleCollapseContextMenu(this.props.node);
                      this.forceUpdate();
                    }}
                  />
                </span>
              </div>
            }
            {this.props.treeLinkStore.inEditMode &&
              <span
                className={styles.treeLinkNodeContextMenu}
                style={{
                  float: "right",
                  cursor: "move"
                }}
                onMouseDown={() => {
                  const parentNode: any = document.getElementById(`IA_TreeLink_${this.props.node.config.id}`);
                  parentNode.setAttribute('draggable', 'true');
                }}
                onMouseUp={() => {
                  const parentNode: any = document.getElementById(`IA_TreeLink_${this.props.node.config.id}`);
                  parentNode.setAttribute('draggable', 'false');
                }}
              >
                <IAIcon
                  title="Move"
                  size={18}
                  style={{
                    position: "inherit",
                    padding: 5
                  }}
                  color={this.props.treeLinkStore.editHighlightColor}
                />
              </span>
            }
            {this.props.node.config.description &&
              <div
                style={{
                  position: "relative",
                  float: "left",
                  fontSize: "14px",
                  paddingBottom: 10,
                  paddingLeft: 1,
                  clear: "both",
                  color: this.props.treeLinkStore.textColor ? this.props.treeLinkStore.textColor : (this.props.treeLinkStore.hasDarkBackground ? "#ffffff" : "#333333"),
                  fontFamily: this.props.fontSlots?.body?.fontFamily
                }}
                onDragEnter={() => {
                  this.props.onDragEnter(this.props.node);
                }}
                onDragLeave={() => {
                  this.props.onDragEnter(undefined);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
              >
                {this.props.node.config.description}
              </div>
            }
            <div
              style={{
                position: "relative",
                left: "calc(100% - 30px)",
                width: 100,
                color: Helper.getTextColorBasedOnBackgroundColorDarkness(this.props.treeLinkStore.editHighlightColor, this.props.treeLinkStore.editHighlightColor, "#333333")
              }}>
              {this.props.node.config.isContextMenuExpanded && this.props.treeLinkStore.inEditMode &&
                <ul ref={this.ref} style={{
                  display: "block",
                  paddingLeft: "inherit",
                  position: "absolute",
                  width: "100px",
                  marginTop: isHeader ? 26 : 26,
                  marginLeft: 2,
                  fontSize: ".75rem",
                  lineHeight: "1.25rem",
                  backgroundColor: "white",
                  boxShadow: "0px 2px 4px 0px rgba(0,0,0,0.1)",
                  zIndex: 99,
                  padding: 10,
                  cursor: "pointer"
                }}>
                  {commandBarItems}
                </ul>
              }
            </div>
          </div>
          {this.props.showDropZoneForId === this.props.node.config.id && isHeader &&
            <div
              className={styles.IA_showDropZone}
              style={{
                borderColor: this.props.treeLinkStore.editHighlightColor
              }}
            />
          }
          {this.props.node.children && this.props.node.children.length > 0 && this.state.expanded &&
            this.props.node.children.map((child, index) => {
              if (this.props.treeLinkStore.amountOfFirstLevelItemsToShow === undefined || index < this.props.treeLinkStore.amountOfFirstLevelItemsToShow || this.state.showAllFirstLevelItems || this.props.treeLinkStore.inEditMode || this.props.node.config.nodeLevel !== 0) {
                return (
                  <div
                    key={`treeLinkNode_${index}`}
                    className={child.config.nodeLevel > 1 ? styles.treeNodeChildren : undefined}
                  >
                    <TreeLinkNode
                      node={child}
                      rerender={() => this.props.rerender()}
                      fontSlots={this.props.fontSlots}
                      onClick={(node: ITreeLinkNode) => this.props.onClick(node)}
                      onDragEnter={(node: ITreeLinkNode) => this.props.onDragEnter(node)}
                      onDrag={(node: ITreeLinkNode) => this.props.onDrag(node)}
                      onDrop={(node: ITreeLinkNode) => this.props.onDrop(node)}
                      showDropZoneForId={this.props.showDropZoneForId}
                      index={index}
                      showPage={(page: string) => this.props.showPage(page)}
                    />
                  </div>
                );
              }
            })
          }
        </div>
        {isHeader && this.props.node.children && this.props.node.children.length > this.props.treeLinkStore.amountOfFirstLevelItemsToShow && !this.props.treeLinkStore.inEditMode && this.state.expanded &&
          <div
            style={{
              float: "left",
              width: "100%",
              cursor: "pointer"
            }}
            onClick={() => {
              this.setState({ showAllFirstLevelItems: !this.state.showAllFirstLevelItems });
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                lineHeight: "30px",
                color: this.props.treeLinkStore.highlightColor,
                width: "100%",
                paddingLeft: 2,
                boxSizing: "border-box"
              }}
            >
              {this.state.showAllFirstLevelItems ? this.localizationService.strings.TreeLink_Minimize : this.localizationService.strings.TreeLink_SeeAll}
            </div>
          </div>
        }
        {
          isHeader && this.props.treeLinkStore.inEditMode &&
          <div
            style={{
              height: 20,
              float: "left",
              width: "100%"
            }}
          >
            <hr style={{
              height: .5,
              width: "calc(50% - 19px)",
              borderWidth: 0,
              color: this.props.treeLinkStore.editHighlightColor,
              backgroundColor: this.props.treeLinkStore.editHighlightColor,
              marginTop: 15,
              boxShadow: "0px .5px 1px 0px rgba(0,0,0,0.1)",
              float: "left"
            }}
            />
            <IAIcon
              title="Plus"
              size={22}
              style={{
                position: "relative",
                top: 4,
                cursor: "pointer",
                width: 20,
                marginLeft: 8,
                marginRight: 10,
                float: "left"
              }}
              color={this.props.treeLinkStore.editHighlightColor}
              onClick={() => {
                this.props.treeLinkStore.createNewNode(this.props.node);
              }}
            />
            <hr style={{
              height: .5,
              width: "calc(50% - 19px)",
              borderWidth: 0,
              color: this.props.treeLinkStore.editHighlightColor,
              backgroundColor: this.props.treeLinkStore.editHighlightColor,
              marginTop: 15,
              boxShadow: "0px .5px 1px 0px rgba(0,0,0,0.1)",
              float: "left"
            }}
            />
          </div>
        }
        {this.props.showDropZoneForId === this.props.node.config.id && this.props.node.config.nodeLevel !== 0 &&
          <div
            className={styles.IA_showDropZone}
            style={{
              borderColor: this.props.treeLinkStore.editHighlightColor
            }}
          />
        }
      </>
    );
  }
}